<template>
  <div class="survey-v3">
    <!-- Loading -->
    <div v-if="loadingSurvey" class="survey-card survey-card--loading">
      <v-skeleton-loader type="card-heading, list-item-three-line"></v-skeleton-loader>
    </div>

    <!-- Error state -->
    <div v-else-if="cardPhase === 'survey' && errorMessage" class="survey-card">
      <SurveyCardHeader
        :showBackButton="showBackButton"
        :displayName="displayName"
        @back="$emit('back')"
      />
      <div class="survey-card__content survey-card__content--error">
        <Typography as="p" variant="subtitle-1">Whoops! Something went wrong</Typography>
        <Typography as="p" variant="body-1">{{ errorMessage }}</Typography>
        <Button class="survey-card__cta" @click="getQuestions"> Try Again </Button>
      </div>
    </div>

    <!-- Intro: LearningCardContent + Start Survey on last intro card -->
    <template v-else-if="cardPhase === 'intro'">
      <div class="survey-card full-width">
        <SurveyCardHeader
          :showBackButton="showBackButton"
          :displayName="displayName"
          @back="$emit('back')"
        />
        <div class="survey-card__content">
          <v-divider />
          <LearningCardContent v-if="currentCard" :card="currentCard" context="survey" />
          <v-divider />
          <div class="survey-card__actions pt-5">
            <Button class="survey-card__cta" :loading="postingLearningLog" @click="goToNextCard">
              {{ isLastIntroCard ? "Start Survey" : "Next" }}
            </Button>
          </div>
        </div>
      </div>
    </template>

    <!-- Survey: Figma layout - progress, question, divider, question component, Next -->
    <template
      v-else-if="cardPhase === 'survey' && questionList.length && questionList[questionIndex]"
    >
      <div class="survey-card">
        <SurveyCardHeader
          :showBackButton="showBackButton"
          :displayName="displayName"
          @back="$emit('back')"
        />
        <div class="survey-card__content survey-card__content--questions">
          <!-- Progress: "1 of 3" - Figma style_P5P50L, #077DAF -->
          <Typography as="span" variant="body-1" class="survey-card__progress">
            {{ questionIndex + 1 }} of {{ questionList.length }}
          </Typography>
          <!-- Question UI (title/helper come from component; we wrap in consistent spacing) -->
          <div class="survey-card__question-block">
            <QuizQuestionRenderer
              :question="questionList[questionIndex]"
              :questionIndex="questionIndex"
              :questionCount="questionList.length"
              :learningCardAssociationId="currentCard.learningCardAssociationId"
              :isSurveyQuestion="true"
              :surveyClientId="surveyClientId"
              :whistleToken="whistleToken"
              :enteredEmail="email"
              context="survey"
              :preview="preview"
              @next-question="goToNextQuestion"
              @complete-quiz="completeQuiz"
            />
          </div>
        </div>
      </div>
    </template>

    <!-- Survey phase but no question (e.g. still loading or no questions) -->
    <div
      v-else-if="
        cardPhase === 'survey' &&
        !loadingSurvey &&
        (!questionList.length || !questionList[questionIndex])
      "
      class="survey-card"
    >
      <SurveyCardHeader
        :showBackButton="showBackButton"
        :displayName="displayName"
        @back="$emit('back')"
      />
      <div class="survey-card__content">
        <v-divider />
        <Typography as="p" variant="body-1"
          >We had trouble finding your survey questions.</Typography
        >
      </div>
    </div>

    <!-- Conclusion: LearningCardContent + actions -->
    <template v-else-if="cardPhase === 'conclusion'">
      <div class="survey-card">
        <SurveyCardHeader
          :showBackButton="showBackButton"
          :displayName="displayName"
          @back="$emit('back')"
        />
        <div class="survey-card__content">
          <v-divider />
          <LearningCardContent v-if="currentCard" :card="currentCard" context="survey" />
          <v-divider />
          <div class="survey-card__actions pt-2">
            <Button
              v-if="!whistleToken && !preview"
              class="survey-card__cta"
              :disabled="postingLearningLog"
              @click="$emit('exit')"
            >
              Go to Programs
            </Button>
            <Button
              v-if="isSurveyRepeatable && !preview"
              class="survey-card__cta"
              :disabled="postingLearningLog"
              @click="retakeSurvey"
            >
              Take Again
            </Button>
          </div>
        </div>
      </div>
    </template>

    <LoadingDialog
      v-if="cardPhase === 'survey'"
      :showDialog="loadingSurvey && !questionList.length"
      header="Loading Survey"
      line1="Please wait"
    />
  </div>
</template>

<script>
import LearningCardContent from "@/components/learning/LearningCardContent.vue";
import SurveyCardHeader from "@/components/learning/Survey/SurveyCardHeader.vue";
import Button from "@/components/generic/Button.vue";
import Typography from "@/components/generic/Typography.vue";
import LoadingDialog from "@/components/loadingDialog.vue";
import QuizQuestionRenderer from "@/components/learning/QuizQuestion/QuizQuestionRenderer.vue";
import LearningService from "@/services/LearningService";

export default {
  name: "SurveyV3",
  components: {
    LearningCardContent,
    SurveyCardHeader,
    Button,
    Typography,
    LoadingDialog,
    QuizQuestionRenderer,
  },
  props: {
    moduleId: Number,
    chapterId: Number,
    survey: Object,
    completedStatus: Boolean,
    displayName: String,
    surveyClientId: Number,
    whistleToken: String,
    email: String,
    /** When false, hides the back button (e.g. for public surveys). */
    showBackButton: { type: Boolean, default: true },
    /**
     * Optional. When advancing cards, call with the card we're leaving.
     * (card, options) => Promise. options may be { returnLog: true, extraLogs: [] } to accumulate and return logs; else posts and resolves.
     */
    postLearningLog: { type: Function, default: null },
    preview: { type: Boolean, default: false },
  },
  data() {
    return {
      loadingSurvey: false,
      questionList: [],
      questionIndex: 0,
      currentLearningCard: null,
      activelyRetaking: false,
      errorMessage: null,
      // Card flow (managed internally)
      currentChapterIndex: 0,
      currentCardIndex: 0,
      surveyCompleted: false,
      postingLearningLog: false,
    };
  },
  computed: {
    currentChapter() {
      const survey = this.survey;
      const chIndex = this.currentChapterIndex;
      if (survey?.LearningChapters?.length && chIndex != null && chIndex >= 0) {
        return survey.LearningChapters[chIndex] || null;
      }
      return null;
    },
    currentCard() {
      const cardIndex = this.currentCardIndex;
      const chapter = this.currentChapter;
      if (
        chapter?.LearningCards?.length &&
        cardIndex != null &&
        cardIndex >= 0 &&
        cardIndex < chapter.LearningCards.length
      ) {
        return chapter.LearningCards[cardIndex];
      }
      return null;
    },
    nextCard() {
      const survey = this.survey;
      const chIndex = this.currentChapterIndex;
      const cardIndex = this.currentCardIndex;
      const chapter = this.currentChapter;
      if (!chapter?.LearningCards?.length || cardIndex == null) return null;
      const cards = chapter.LearningCards;
      if (cardIndex + 1 < cards.length) return cards[cardIndex + 1];
      if (survey?.LearningChapters?.length && chIndex + 1 < survey.LearningChapters.length) {
        const nextChapter = survey.LearningChapters[chIndex + 1];
        const nextCards = nextChapter?.LearningCards;
        return nextCards?.length ? nextCards[0] : null;
      }
      return null;
    },
    isLastIntroCard() {
      if (this.cardPhase !== "intro") return false;
      const next = this.nextCard;
      if (!next) return false;
      return (
        next.type === "survey" ||
        (Array.isArray(next.LearningQuizQuestions) && next.LearningQuizQuestions.length > 0)
      );
    },
    isSurveyRepeatable() {
      if (!this.survey || !this.survey.LearningChapters) return false;
      return this.survey.LearningChapters.some((ch) =>
        ch.LearningCards.some((card) => card.repeatable === true)
      );
    },
    cardPhase() {
      if (this.currentChapter && this.currentChapter.conclusion) return "conclusion";
      const card = this.currentCard;
      if (!card) return "intro";
      const isSurveyCard =
        card.type === "survey" ||
        (Array.isArray(card.LearningQuizQuestions) && card.LearningQuizQuestions.length > 0);
      return isSurveyCard ? "survey" : "intro";
    },
    quizCompleted: {
      get() {
        return this.completedStatus ?? this.surveyCompleted;
      },
      set(val) {
        this.surveyCompleted = val;
        this.$emit("update-completed", val);
      },
    },
  },
  watch: {
    survey: {
      immediate: true,
      handler(survey) {
        if (survey?.LearningChapters?.length) this.initSurveyProgress();
      },
    },
    cardPhase: {
      handler(phase) {
        if (phase === "survey") this.getQuestions();
      },
    },
    currentCard: {
      handler(card) {
        if (this.cardPhase === "survey" && card) this.getQuestions();
      },
    },
  },
  mounted() {
    if (this.survey?.LearningChapters?.length) this.initSurveyProgress();
    if (this.cardPhase === "survey") this.getQuestions();
  },
  methods: {
    initSurveyProgress() {
      const survey = this.survey;
      if (!survey?.LearningChapters?.length) return;
      let completeBool = false;
      survey.LearningChapters.forEach((chapter, chapterIndex) => {
        chapter.LearningCards.forEach((card, cardIndex) => {
          if (
            (!card.latestLearningLog ||
              (card.latestLearningLog && !card.latestLearningLog.completed)) &&
            !completeBool
          ) {
            this.currentChapterIndex = chapterIndex;
            this.currentCardIndex = cardIndex;
            this.surveyCompleted = card.latestLearningLog
              ? card.latestLearningLog.completed
              : false;
            completeBool = true;
          }
          if (
            !completeBool &&
            chapterIndex === survey.LearningChapters.length - 1 &&
            cardIndex === chapter.LearningCards.length - 1
          ) {
            this.currentChapterIndex = chapterIndex;
            if (chapter.LearningCards?.length) {
              this.currentCardIndex = cardIndex;
              this.surveyCompleted = true;
            }
          }
        });
      });
    },
    async goToNextCard() {
      const chapters = this.survey?.LearningChapters;
      if (!chapters?.length || !this.currentCard) return;
      const ch = chapters[this.currentChapterIndex];
      const cards = ch?.LearningCards;
      const nextIndexInChapter = this.currentCardIndex + 1;
      this.postingLearningLog = true;
      try {
        if (this.postLearningLog) {
          await this.postLearningLog(this.currentCard, { returnLog: false, extraLogs: [] });
        }
        if (cards?.length && nextIndexInChapter < cards.length) {
          this.currentCardIndex = nextIndexInChapter;
        } else {
          await this.goToNextChapter();
        }
      } catch (err) {
        console.error("Error posting learning log", err);
        this.$store.dispatch("showError", {
          title: "Error saving progress",
          message: "Please try again",
        });
      } finally {
        this.postingLearningLog = false;
      }
    },
    async goToNextChapter() {
      const survey = this.survey;
      if (!survey?.LearningChapters?.length) return;
      const nextChapter = this.currentChapterIndex + 1;
      this.postingLearningLog = true;
      try {
        if (nextChapter < survey.LearningChapters.length - 1) {
          if (this.postLearningLog) {
            await this.postLearningLog(this.currentCard, { returnLog: false, extraLogs: [] });
          }
          this.currentChapterIndex = nextChapter;
          this.currentCardIndex = 0;
        } else {
          let logs = [];
          if (this.postLearningLog) {
            logs = await this.postLearningLog(this.currentCard, {
              returnLog: true,
              extraLogs: [],
            });
          }
          this.currentChapterIndex = nextChapter;
          this.currentCardIndex = 0;
          const conclusionCard =
            survey.LearningChapters[this.currentChapterIndex]?.LearningCards?.[0];
          if (conclusionCard && this.postLearningLog) {
            await this.postLearningLog(conclusionCard, { returnLog: false, extraLogs: logs });
          }
          this.surveyCompleted = true;
          this.$emit("surveyCompleted");
        }
      } catch (err) {
        console.error("Error posting learning log", err);
        this.$store.dispatch("showError", {
          title: "Error saving progress",
          message: "Please try again",
        });
      } finally {
        this.postingLearningLog = false;
      }
    },
    async getQuestions() {
      if (this.cardPhase !== "survey" || !this.currentCard) return;
      this.loadingSurvey = true;
      this.errorMessage = null;
      const card = this.currentCard;
      try {
        const qCard = await LearningService.getLearningCard(
          card.learningCardId,
          null,
          this.whistleToken
        );
        this.currentLearningCard = qCard;
        qCard.LearningQuizQuestions.forEach((question) => {
          question.LearningQuizResponses = (question.LearningQuizResponses || [])
            .filter((x) => x.learningCardAssocationId == card.learningCardAssociationId)
            .sort((a, b) => (b.attempt || 0) - (a.attempt || 0));
        });
        this.questionList = (qCard.LearningQuizQuestions || []).sort(
          (a, b) => (a.order || 0) - (b.order || 0)
        );
        let quizCompleted = false;
        if (card.latestLearningLog && !this.activelyRetaking) {
          quizCompleted = card.latestLearningLog.completed;
        } else if (this.whistleToken !== undefined && !this.activelyRetaking) {
          quizCompleted = (card.LearningLogs && card.LearningLogs.length > 0) || false;
        }
        this.quizCompleted = quizCompleted;
        if (!quizCompleted && qCard.numRandomQuestions == null && !this.activelyRetaking) {
          let loadQuizIndex = null;
          this.questionList.forEach((x, i) => {
            if (
              loadQuizIndex === null &&
              (!x.LearningQuizResponses || x.LearningQuizResponses.length === 0)
            ) {
              loadQuizIndex = i;
            }
          });
          this.questionIndex = loadQuizIndex != null ? loadQuizIndex : 0;
        } else if (this.activelyRetaking) {
          let qCount = this.questionList[0]?.LearningQuizResponses?.length || 0;
          let startIndex = 0;
          for (let i = 1; i < this.questionList.length; i++) {
            const len = this.questionList[i].LearningQuizResponses?.length || 0;
            if (len < qCount) {
              qCount = len;
              startIndex = i;
              break;
            }
          }
          this.questionIndex = startIndex;
        }
      } catch (err) {
        console.error("Error getting survey", err);
        this.errorMessage = "Please try again.";
      } finally {
        this.loadingSurvey = false;
      }
    },
    goToNextQuestion() {
      this.questionIndex += 1;
    },
    completeQuiz() {
      this.goToNextChapter();
    },
    retakeSurvey() {
      this.surveyCompleted = false;
      this.activelyRetaking = true;
      this.currentChapterIndex = 0;
      this.currentCardIndex = 0;
      this.questionIndex = 0;
      this.questionList = [];
      if (this.survey?.LearningChapters?.length) {
        this.getQuestions();
      }
      this.$emit("retakeSurvey");
    },
  },
};
</script>

<style scoped>
.survey-v3 {
  min-height: 200px;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 0 0 auto;
}

.survey-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 24px 24px 32px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 32px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
  align-self: stretch;
}

@media screen and (max-width: 599px) {
  .survey-card {
    padding: 16px 16px 24px;
    gap: 24px;
  }

  .survey-card__content {
    gap: 10px;
  }
}

@media screen and (max-width: 374px) {
  .survey-card {
    padding: 0px 0px 20px;
    gap: 20px;
  }

  .survey-card__content {
    gap: 8px;
  }
}

.survey-card--loading {
  min-height: 200px;
}

.survey-card__content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  min-width: 0;
  padding: 0 16px;
  box-sizing: border-box;
}

@media screen and (min-width: 600px) {
  .survey-card__content {
    padding: 0 24px;
  }
}

@media screen and (min-width: 960px) {
  .survey-card__content {
    padding: 0 40px;
  }
}

/* Survey question layout: left-aligned, gap 20px (Figma content frame) */
.survey-card__content--questions {
  align-items: stretch;
  gap: 20px;
  text-align: left;
}

.survey-card__content--error {
  align-items: center;
  text-align: center;
}

/* Progress "1 of 3" - Figma style_P5P50L, #077DAF */
.survey-card__progress {
  color: var(--v-brandPrimary-base);
  font-weight: 350;
  font-size: 16px;
  line-height: 1.5em;
}

/* Answers block: column, gap 20px, padding 0 16px (Figma) */
.survey-card__question-block {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 20px;
  box-sizing: border-box;
}

.survey-card__actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
}

.survey-card__cta {
  border-radius: 18px !important;
  padding: 8px 20px;
  text-transform: uppercase;
  letter-spacing: 0.089em;
}
</style>
